// Central Translation Utility for E-Commerce App
const translationCache = new Map();

/**
 * Translates single text string from sourceLang to targetLang.
 * @param {string} text 
 * @param {string} targetLang 
 * @param {string} sourceLang 
 * @returns {Promise<string>}
 */
export const translateText = async (text, targetLang = 'en', sourceLang = 'en') => {
  if (!text || typeof text !== 'string' || !text.trim()) return text || '';
  const cleanTarget = (targetLang || 'en').toLowerCase().trim();
  if (cleanTarget === 'en' || cleanTarget === sourceLang.toLowerCase().trim()) {
    return text;
  }

  const cacheKey = `${cleanTarget}::${text.trim()}`;
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  // Check sessionStorage cache if available
  try {
    const cached = sessionStorage.getItem(`tr_${cacheKey}`);
    if (cached) {
      translationCache.set(cacheKey, cached);
      return cached;
    }
  } catch (e) {
    // sessionStorage not available or error
  }

  // 1. Try Google Translate free endpoint (gtx)
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${cleanTarget}&dt=t&q=${encodeURIComponent(text)}`;
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      if (data && data[0] && Array.isArray(data[0])) {
        const translated = data[0].map((item) => (item && item[0] ? item[0] : '')).join('');
        if (translated && !translated.includes('MYMEMORY WARNING')) {
          translationCache.set(cacheKey, translated);
          try {
            sessionStorage.setItem(`tr_${cacheKey}`, translated);
          } catch (e) {}
          return translated;
        }
      }
    }
  } catch (err) {
    // Failover to next translation service
  }

  // 2. Fallback to MyMemory API
  try {
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${sourceLang}|${cleanTarget}`;
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      if (data && data.responseData && data.responseData.translatedText) {
        const translated = data.responseData.translatedText;
        if (
          !translated.includes('MYMEMORY WARNING') &&
          !translated.includes('IS AN INVALID TARGET LANGUAGE') &&
          !translated.includes('QUERY LENGTH LIMIT EXCEEDED')
        ) {
          translationCache.set(cacheKey, translated);
          try {
            sessionStorage.setItem(`tr_${cacheKey}`, translated);
          } catch (e) {}
          return translated;
        }
      }
    }
  } catch (err) {
    // Fallback failed
  }

  return text;
};

/**
 * Translates an array of text strings.
 * @param {string[]} texts 
 * @param {string} targetLang 
 * @param {string} sourceLang 
 * @returns {Promise<string[]>}
 */
export const translateBatch = async (texts, targetLang = 'en', sourceLang = 'en') => {
  if (!texts || !Array.isArray(texts)) return [];
  if (!targetLang || targetLang === 'en') return texts;
  return Promise.all(texts.map((t) => translateText(t, targetLang, sourceLang)));
};
